import React, { createContext, useContext, useState } from 'react';
import type { BundleId } from '../data/pricing';
import { addonQualifies, bundleSuggestionForIndividual, upgradeTarget } from '../utils/pricing';
import { useCart } from './CartContext';
import { track } from '../utils/analytics';
export type FunnelState = {
  step: 'upgrade';
  bundleId: BundleId;
  stepIndex: number;
  totalSteps: number;
} | {
  step: 'expand';
  bundleId: BundleId;
  stepIndex: number;
  totalSteps: number;
} | {
  step: 'bundleSuggest';
  qty: number;
  stepIndex: number;
  totalSteps: number;
} | {
  step: 'done';
} | null;
type FunnelContextValue = {
  state: FunnelState;
  startSystem: (bundleId: BundleId) => void;
  startIndividual: (qty: number) => void;
  setState: (s: FunnelState) => void;
  close: () => void;
};
const FunnelContext = createContext<FunnelContextValue | null>(null);

/**
 * Orchestrates optional offers between "Add to cart" and the cart.
 * Never more than two optional steps; every step is declinable.
 */
export function FunnelProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<FunnelState>(null);
  const {
    addIndividual,
    addSystem
  } = useCart();
  const startSystem = (bundleId: BundleId) => {
    track('bundle_selected', {
      bundleId
    });
    const hasUpgrade = Boolean(upgradeTarget(bundleId));
    const canExpand = addonQualifies(bundleId);
    const totalSteps = (hasUpgrade ? 1 : 0) + (canExpand ? 1 : 0);
    if (hasUpgrade) return setState({
      step: 'upgrade',
      bundleId,
      stepIndex: 1,
      totalSteps
    });
    if (canExpand) return setState({
      step: 'expand',
      bundleId,
      stepIndex: 1,
      totalSteps
    });
    addSystem(bundleId, 0);
    setState({
      step: 'done'
    });
  };
  const startIndividual = (qty: number) => {
    if (bundleSuggestionForIndividual(qty)) {
      return setState({
        step: 'bundleSuggest',
        qty,
        stepIndex: 1,
        totalSteps: 2
      });
    }
    addIndividual(qty);
    setState({
      step: 'done'
    });
  };
  return <FunnelContext.Provider value={{
    state,
    startSystem,
    startIndividual,
    setState,
    close: () => setState(null)
  }} data-id="element-940" data-mp="1sa05qvq3lnwq">
      {children}
    </FunnelContext.Provider>;
}
export function useFunnel() {
  const ctx = useContext(FunnelContext);
  if (!ctx) throw new Error('useFunnel must be used within FunnelProvider');
  return ctx;
}